import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';

export default function SwipeToConfirm({ 
  onConfirm, 
  text = "Glisser pour valider l'étape",
  loadingText = "Mise à jour...", 
  isLoading = false,
  className 
}) {
  const [value, setValue] = useState(0);
  const [isSuccess, setIsSuccess] = useState(false);
  const containerRef = useRef(null);
  const handleWidth = 56;

  useEffect(() => {
    if (isLoading) {
      setValue(100);
    } else if (!isSuccess) {
      setValue(0);
    }
  }, [isLoading, isSuccess]);

  const handleChange = (e) => {
    if (isLoading || isSuccess) return;
    setValue(Number(e.target.value));
  };

  // Committing on pointer release only made this control unreachable by
  // keyboard: arrow keys and End moved the handle to 100 and nothing ever
  // fired, so the primary action of the app could not be completed without a
  // mouse. Releasing a key is the keyboard equivalent of releasing the pointer.
  const handleRelease = () => {
    if (isLoading || isSuccess) return;
    if (Number(value) > 90) {
      setValue(100);
      setIsSuccess(true);
      onConfirm();
      setTimeout(() => setIsSuccess(false), 3000);
    } else {
      setValue(0);
    }
  };

  return (
    <div 
      ref={containerRef}
      className={cn("relative w-full h-16 bg-info/15 rounded-full overflow-hidden flex items-center justify-center select-none shadow-inner", className)}
    >
      <div 
        className="absolute top-0 left-0 h-full bg-info transition-all ease-out flex items-center justify-end"
        style={{ width: `calc(${value}% + ${handleWidth}px * (100 - ${value})/100)` }}
      ></div>
      
      {/* Background text */}
      <span className="absolute z-10 text-info font-bold ml-8 pointer-events-none select-none transition-opacity" style={{ opacity: Math.max(0, 1 - (value / 30)) }}>
        {text}
      </span>
      
      {/* Success/Loading text */}
      <span className="absolute z-10 text-white font-bold pointer-events-none select-none transition-opacity" style={{ opacity: value > 50 ? 1 : 0 }}>
        {isLoading ? (
          <span className="flex items-center"><Loader2 className="size-5 mr-2 animate-spin" /> {loadingText}</span>
        ) : isSuccess ? (
          "Étape validée !"
        ) : (
          text
        )}
      </span>

      <input 
        type="range" 
        min="0" 
        max="100" 
        value={value}
        onChange={handleChange}
        onMouseUp={handleRelease}
        onTouchEnd={handleRelease}
        onKeyUp={handleRelease}
        disabled={isLoading || isSuccess}
        aria-label={`${text} (flèches ou Fin pour aller au bout, puis relâcher)`}
        aria-valuetext={
          isSuccess ? 'Étape validée' : `${Math.round(Number(value))} % — glisser jusqu'au bout pour valider`
        }
        className="absolute w-full h-full opacity-0 cursor-pointer z-20"
      />
      
      <div 
        className="absolute top-1 bottom-1 w-14 bg-card rounded-full shadow-md flex items-center justify-center pointer-events-none z-10 transition-all ease-out"
        style={{ left: `calc(${value}% - ${value * handleWidth / 100}px + 4px)` }}
      >
        <ArrowRight className="size-6 text-info" />
      </div>
    </div>
  );
}
